import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { NoteFragment, useUpdateNotePreparationMutation } from '@treams/treams-network';
import { FlexGap, Subheading, TextArea, tokens } from '@treams/treams-ui';

import NotePanel from './NotePanel';

const Status = styled.span`
	font-size: ${tokens.typography.sizes.small}px;
	color: ${tokens.text.secondary};
`;

const DEBOUNCE_MS = 600;

export default function NotePreparationPanel({ note }: { note: NoteFragment }) {
	const { t } = useTranslation();
	const [updatePreparation] = useUpdateNotePreparationMutation();

	const [value, setValue] = useState(note.preparation ?? '');
	const [saving, setSaving] = useState(false);
	const [savedAt, setSavedAt] = useState<Date | undefined>();
	const lastPersisted = useRef(note.preparation ?? '');
	const latestValue = useRef(value);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		latestValue.current = value;
	}, [value]);

	const flush = useCallback(
		async ({ keepalive = false }: { keepalive?: boolean } = {}) => {
			if (timer.current) {
				clearTimeout(timer.current);
				timer.current = null;
			}
			const pending = latestValue.current;
			const previous = lastPersisted.current;
			if (pending === previous) {
				return;
			}
			// Optimistically mark as persisted so concurrent flushes don't double-send.
			lastPersisted.current = pending;
			setSaving(true);
			try {
				await updatePreparation({
					variables: {
						input: {
							noteID: note.id,
							preparation: pending,
						},
					},
					// `keepalive` lets the request complete even if the page is being unloaded.
					context: keepalive ? { fetchOptions: { keepalive: true } } : undefined,
				});
				setSavedAt(new Date());
			} catch (error) {
				lastPersisted.current = previous;
				throw error;
			} finally {
				setSaving(false);
			}
		},
		[note.id, updatePreparation],
	);

	useEffect(() => {
		if (value === lastPersisted.current) {
			return;
		}

		if (timer.current) {
			clearTimeout(timer.current);
		}
		timer.current = setTimeout(() => {
			void flush();
		}, DEBOUNCE_MS);

		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, [flush, value]);

	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				void flush({ keepalive: true });
			}
		};
		const onPageHide = () => {
			void flush({ keepalive: true });
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('pagehide', onPageHide);
		return () => {
			document.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('pagehide', onPageHide);
		};
	}, [flush]);

	useEffect(() => {
		// Flush any pending edits when the panel unmounts (e.g. closing the modal).
		return () => {
			void flush();
		};
	}, [flush]);

	return (
		<NotePanel>
			<FlexGap alignItems="center" justifyContent="space-between" gap={8}>
				<Subheading>{t('oneOnOne.preparation.title')}</Subheading>
				{saving ? (
					<Status>{t('common.saving')}</Status>
				) : savedAt ? (
					<Status>{t('common.saved')}</Status>
				) : null}
			</FlexGap>
			<TextArea
				minRows={4}
				maxRows={12}
				value={value}
				onChange={(next) => setValue(next)}
				placeholder={t('oneOnOne.preparation.placeholder')}
			/>
		</NotePanel>
	);
}
